import React from 'react';
import classnames from 'classnames';

import styles from './progress-bar.css';

export enum ProgressBarColors {
  UnbounceBlue = 'unbounceBlue',
  DodgerBlue = 'dodgerBlue',
  Orange = 'orange',
  RadicalRed = 'radicalRed',
  Shamrock = 'shamrock',
  Alto = 'alto',
  Emperor = 'emperor'
}

interface Props {
  /** Number from 0 - 100 that represents the percentage completed */
  percentCompleted: number;
  /** Color of the progress bar */
  color?: ProgressBarColors;
  /** Text to display inside the progress bar */
  label?: string;
  /** Provides visual elements for usage on a dark environment */
  darkEnv?: boolean;
  /** Provides visual elements that appear as a large progress bar */
  large?: boolean;
  className?: string;
}

const defaultColor = (progressPercent: number) => {
  if (progressPercent < 75) {
    return ProgressBarColors.UnbounceBlue;
  }
  if (progressPercent < 100) {
    return ProgressBarColors.Orange;
  }
  return ProgressBarColors.RadicalRed;
};

const getDarkEnvColor = (color: ProgressBarColors) => {
  switch (color) {
    case ProgressBarColors.UnbounceBlue:
      return ProgressBarColors.DodgerBlue;
    case ProgressBarColors.Alto:
      return ProgressBarColors.Emperor;
    default:
      return color;
  }
};

const ProgressBar = (props: Props) => {
  const { percentCompleted, label, darkEnv, large, className, color, ...rest } = props;

  let progressBarColor = color || defaultColor(percentCompleted);

  if (darkEnv) {
    progressBarColor = getDarkEnvColor(progressBarColor);
  }

  const progressBarClasses = classnames(styles.progressBar, className, {
    [styles.darkEnv]: darkEnv,
    [styles.large]: large
  });
  const percentCompletedClasses = classnames(styles.percentCompleted, styles[progressBarColor]);
  return (
    <div className={progressBarClasses} {...rest}>
      <div className={percentCompletedClasses} style={{ width: `${percentCompleted}%` }} />
      <div className={styles.labelWrapper}>
        {label && <div className={styles.label}>{label}</div>}
      </div>
    </div>
  );
};

ProgressBar.defaultProps = {
  darkEnv: false,
  large: false
};

export default ProgressBar;
